import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"

/**
 * Runs `onReentry` when the user returns to a section (but not on first mount).
 *
 * The hub keeps sections mounted while you navigate elsewhere and re-navigates the
 * section's MemoryRouter when it becomes active again — so a section's components
 * never unmount and never refetch on their own. That re-navigation changes the
 * router location key, which we treat as the "section became active again" signal.
 *
 * Use it to reload data that may have gone stale while the section was inactive.
 */
export function useSectionReentry(onReentry: () => void) {
  const { key } = useLocation()
  const isFirstRender = useRef(true)
  const callbackRef = useRef(onReentry)
  callbackRef.current = onReentry

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    callbackRef.current()
  }, [key])
}
